import { useCallback, useEffect, useRef, useState } from 'react';
import {
  VOICE_LANGUAGES,
  getSpeechRecognitionLang,
  getSpeechSynthesisLang,
  getStoredVoiceLanguage,
  setStoredVoiceLanguage,
} from '../utils/voiceLanguages';

/**
 * Shared Web Speech API hook for voice-first search and conversational booking.
 * Supports en-IN, kn-IN, hi-IN recognition and speech synthesis output.
 */
export function useVoiceBooking({
  language: controlledLanguage,
  onLanguageChange,
  onTranscript,
  onError,
  interimResults = true,
} = {}) {
  const [internalLang, setInternalLang] = useState(getStoredVoiceLanguage);
  const language = controlledLanguage ?? internalLang;

  const setLanguage = useCallback(
    (code) => {
      setStoredVoiceLanguage(code);
      if (onLanguageChange) onLanguageChange(code);
      else setInternalLang(code);
    },
    [onLanguageChange]
  );

  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');

  const recognitionRef = useRef(null);
  const onTranscriptRef = useRef(onTranscript);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onTranscriptRef.current = onTranscript;
  }, [onTranscript]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    setIsSupported(!!SpeechRecognition);
    if (!SpeechRecognition) return undefined;

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = interimResults;
    recognition.maxAlternatives = 3;
    recognition.lang = getSpeechRecognitionLang(language);

    recognition.onresult = (event) => {
      let interim = '';
      let finalText = '';

      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const transcript = event.results[i]?.[0]?.transcript || '';
        if (event.results[i].isFinal) finalText += transcript;
        else interim += transcript;
      }

      setInterimTranscript(interim.trim());

      if (finalText.trim() && onTranscriptRef.current) {
        onTranscriptRef.current(finalText.trim(), { isFinal: true, language });
        setInterimTranscript('');
      }
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      setInterimTranscript('');
      const code = event?.error || 'recognition_error';
      if (code !== 'aborted' && code !== 'no-speech') {
        onErrorRef.current?.(code);
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimTranscript('');
    };

    recognitionRef.current = recognition;

    return () => {
      try {
        recognition.abort();
      } catch {
        /* ignore */
      }
    };
  }, [language, interimResults]);

  const startListening = useCallback(() => {
    const recognition = recognitionRef.current;
    if (!recognition) {
      onErrorRef.current?.('not_supported');
      return false;
    }
    try {
      setInterimTranscript('');
      setIsListening(true);
      recognition.start();
      return true;
    } catch {
      setIsListening(false);
      onErrorRef.current?.('start_failed');
      return false;
    }
  }, []);

  const stopListening = useCallback(() => {
    try {
      recognitionRef.current?.stop();
    } catch {
      /* ignore */
    }
    setIsListening(false);
    setInterimTranscript('');
  }, []);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
      return;
    }
    startListening();
  }, [isListening, startListening, stopListening]);

  const speak = useCallback(
    (text, { lang } = {}) => {
      const synth = window.speechSynthesis;
      if (!synth || !text) return;

      synth.cancel();
      const utterance = new SpeechSynthesisUtterance(String(text).slice(0, 500));
      const targetLang = getSpeechSynthesisLang(lang || language);
      utterance.lang = targetLang;
      utterance.rate = 0.92;
      utterance.pitch = 1;

      const pickVoice = () => {
        const voices = synth.getVoices() || [];
        return (
          voices.find((v) => v.lang === targetLang) ||
          voices.find((v) => v.lang.startsWith(targetLang.split('-')[0])) ||
          voices.find((v) => v.lang.includes('IN')) ||
          null
        );
      };

      const voice = pickVoice();
      if (voice) utterance.voice = voice;

      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);

      synth.speak(utterance);
    },
    [language]
  );

  const stopSpeaking = useCallback(() => {
    window.speechSynthesis?.cancel();
    setIsSpeaking(false);
  }, []);

  return {
    language,
    setLanguage,
    languages: VOICE_LANGUAGES,
    isListening,
    isSpeaking,
    isSupported,
    interimTranscript,
    startListening,
    stopListening,
    toggleListening,
    speak,
    stopSpeaking,
  };
}

export default useVoiceBooking;
